import React, { useState, useEffect } from "react";
import DashboardHeader from "./components/DashboardHeader";
import StatsGrid from "./components/StatsGrid";
import ChartsSection from "./components/ChartsSection";
import AssetTypesGrid from "./components/Assettypesgrid";
import RecentActivity from "./components/Recentactivity";
import FiltersSection from "./components/FiltersSection";
import LoadingSpinner from "./components/LoadingSpinner";
import "./css/page.css";
import {
  DashboardData,
  DashboardFilters,
  UserData,
} from "./interface/page.interface";
import useHookDashboard from "./hooks/useHookDashboard";

const Dashboard: React.FC = () => {
  const {
    clients,
    profile,
    isAdmin,
    typeEquipments,
    statusEquipments,
    equipments,
    clientSelected,
    handleSelectClient,
    typeEquipment,
    handleSelectTypeEquipment,
    firstName,
    assetsByType,
    otsByEquipmentType,
    ots,
    recentActivities
  } = useHookDashboard();

  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [filters, setFilters] = useState<DashboardFilters>({
    dateRange: "30days",
    status: "all",
    client: "all",
    assetType: "all",
  });

  useEffect(() => {
    // Simular carga de datos
    loadDashboardData();
  }, [filters]);

  const loadDashboardData = async (): Promise<void> => {
    setLoading(true);

    // Aquí decodificarías el token JWT para obtener la info del usuario
    // const token = localStorage.getItem('token');
    // const decoded = jwt_decode(token);

    // Simulación de datos del usuario
    const mockUser: UserData = {
      name: "Juan Pérez",
      email: "juan.perez@example.com",
      role: "administrador", // o 'cliente'
      clientId: null, // null si es admin, ID si es cliente
    };

    // Simulación de datos del dashboard
    const mockData: DashboardData = {
      stats: {
        totalAssets: 347,
        activeAssets: 312,
        maintenance: 28,
        alerts: 7,
      },
      assetsByType: [
        { type: "AIRES ACONDICIONADOS", count: 89, icon: "❄️" },
        { type: "APARATOS DE COCINA Y LAVANDERIA", count: 45, icon: "🍳" },
        { type: "EQUIPOS DE COMPUTO", count: 67, icon: "💻" },
        { type: "TABLEROS ELÉCTRICOS", count: 23, icon: "⚡" },
        { type: "DOMÓTICA MÓDULOS", count: 34, icon: "🏠" },
        { type: "EQUIPOS ESPECIALES", count: 18, icon: "⚙️" },
        { type: "CAMARAS", count: 28, icon: "📹" },
        { type: "SEGURIDAD", count: 15, icon: "🔒" },
        { type: "AUDIO Y VIDEO", count: 12, icon: "🎵" },
        { type: "UPS", count: 41, icon: "🔋" },
        { type: "BOMBAS", count: 9, icon: "💧" },
        { type: "EQUIPOS SOLARES", count: 7, icon: "☀️" },
        { type: "GENERADORES", count: 5, icon: "⚡" },
        { type: "DOMÓTICA", count: 21, icon: "🤖" },
      ],
      recentActivity: [
        {
          id: 1,
          type: "maintenance",
          title: "Mantenimiento completado",
          description:
            "Aire Acondicionado Samsung AC-2024 - Cliente: Edificio Central",
          time: "2 horas",
          date: new Date(),
        },
        {
          id: 2,
          type: "new",
          title: "Nuevo activo registrado",
          description: "UPS APC 3000VA - Cliente: Oficina Norte",
          time: "5 horas",
          date: new Date(),
        },
        {
          id: 3,
          type: "alert",
          title: "Mantenimiento próximo",
          description:
            "Generador Caterpillar GEN-001 requiere mantenimiento en 3 días",
          time: "1 día",
          date: new Date(),
        },
        {
          id: 4,
          type: "maintenance",
          title: "Inspección realizada",
          description: "Sistema de Cámaras - Cliente: Plaza Comercial",
          time: "2 días",
          date: new Date(),
        },
      ],
      chartData: {
        monthly: [
          { month: "Ene", activos: 298, mantenimientos: 42 },
          { month: "Feb", activos: 310, mantenimientos: 38 },
          { month: "Mar", activos: 325, mantenimientos: 45 },
          { month: "Abr", activos: 335, mantenimientos: 40 },
          { month: "May", activos: 340, mantenimientos: 48 },
          { month: "Jun", activos: 347, mantenimientos: 52 },
        ],
        monthlyOts: [
          {
            month: "Ene",
            pendientes: 12,
            enProceso: 18,
            completadas: 45,
            canceladas: 3,
            total: 78,
          },
          {
            month: "Feb",
            pendientes: 15,
            enProceso: 22,
            completadas: 38,
            canceladas: 2,
            total: 77,
          },
          {
            month: "Mar",
            pendientes: 8,
            enProceso: 25,
            completadas: 52,
            canceladas: 4,
            total: 89,
          },
          {
            month: "Abr",
            pendientes: 10,
            enProceso: 20,
            completadas: 48,
            canceladas: 2,
            total: 80,
          },
          {
            month: "May",
            pendientes: 14,
            enProceso: 28,
            completadas: 55,
            canceladas: 5,
            total: 102,
          },
          {
            month: "Jun",
            pendientes: 18,
            enProceso: 30,
            completadas: 60,
            canceladas: 3,
            total: 111,
          },
        ],
      },
    };

    // Simular delay de red
    setTimeout(() => {
      setDashboardData(mockData);
      setLoading(false);
    }, 100);
  };

  const handleFilterChange = (
    filterName: keyof DashboardFilters,
    value: string
  ): void => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="dashboard-container fade-in">
      <DashboardHeader profile={profile} firstName={firstName} />

      <FiltersSection
        isAdmin={isAdmin}
        clients={clients}
        typeEquipments={typeEquipments}
        statusEquipments={statusEquipments}
        handleSelectClient={handleSelectClient}
        clientSelected={clientSelected}
        filters={filters}
        onFilterChange={handleFilterChange}
        typeEquipment={typeEquipment}
        handleSelectTypeEquipment={handleSelectTypeEquipment}
      />

      <StatsGrid equipments={equipments} />

      <ChartsSection
        ots={ots}
        otsByEquipmentType={otsByEquipmentType}
      />

      <AssetTypesGrid assetsByType={assetsByType} />

      <RecentActivity activities={recentActivities} />
    </div>
  );
};

export default Dashboard;
